import { useState, useEffect, useRef } from "react";
import { CalendarPlus, ChevronDown, Download } from "lucide-react";
import { toast } from "react-toastify";

const fmtCompactUtc = (d) =>
  d.toISOString().replace(/[-:]/g, "").replace(/\.\d{3}/, "");

const buildIcs = ({ start, end, title, description, location }) => {
  const stamp = fmtCompactUtc(new Date());
  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//R-Link Studio//EN",
    "BEGIN:VEVENT",
    `UID:${stamp}-${Math.random().toString(36).slice(2)}@r-link.com`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${fmtCompactUtc(start)}`,
    `DTEND:${fmtCompactUtc(end)}`,
    `SUMMARY:${title}`,
    `DESCRIPTION:${(description || "").replace(/\n/g, "\\n")}`,
    location ? `LOCATION:${location}` : "",
    location ? `URL:${location}` : "",
    "END:VEVENT",
    "END:VCALENDAR",
  ]
    .filter(Boolean)
    .join("\r\n");
};

const downloadIcs = ({ start, end, title, description, location, filename }) => {
  const ics = buildIcs({ start, end, title, description, location });
  const blob = new Blob([ics], { type: "text/calendar" });
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = `${(filename || title || "event").replace(/[^a-z0-9-_]+/gi, "-")}.ics`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(href);
};

const CalendarShareDropdown = ({
  title,
  description,
  startTime,
  endTime,
  durationMinutes = 60,
  location = "",
  filename,
  buttonClassName,
  label = "Add to Calendar",
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  useEffect(() => {
    if (!open || !menuRef.current) return;
    const id = requestAnimationFrame(() => {
      menuRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  const buildEvent = () => {
    if (!startTime) {
      toast.error("Event start time is not set");
      return null;
    }
    const start = new Date(startTime);
    if (Number.isNaN(start.getTime())) {
      toast.error("Invalid event start time");
      return null;
    }
    let end;
    if (endTime) {
      end = new Date(endTime);
      if (Number.isNaN(end.getTime())) end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    } else {
      end = new Date(start.getTime() + durationMinutes * 60 * 1000);
    }
    return {
      start,
      end,
      title: title || "Event",
      description: description || "",
      location: location || "",
    };
  };

  const openHref = (href) => {
    window.open(href, "_blank", "noopener,noreferrer");
    setOpen(false);
  };

  const handleGoogle = () => {
    const ev = buildEvent();
    if (!ev) return;
    const href =
      `https://calendar.google.com/calendar/render?action=TEMPLATE` +
      `&text=${encodeURIComponent(ev.title)}` +
      `&dates=${fmtCompactUtc(ev.start)}/${fmtCompactUtc(ev.end)}` +
      `&details=${encodeURIComponent(ev.description)}` +
      `&location=${encodeURIComponent(ev.location)}`;
    openHref(href);
  };

  const handleOutlook = () => {
    const ev = buildEvent();
    if (!ev) return;
    const href =
      `https://outlook.office.com/calendar/0/deeplink/compose` +
      `?subject=${encodeURIComponent(ev.title)}` +
      `&startdt=${encodeURIComponent(ev.start.toISOString())}` +
      `&enddt=${encodeURIComponent(ev.end.toISOString())}` +
      `&body=${encodeURIComponent(ev.description)}` +
      `&location=${encodeURIComponent(ev.location)}`;
    openHref(href);
  };

  const handleYahoo = () => {
    const ev = buildEvent();
    if (!ev) return;
    const href =
      `https://calendar.yahoo.com/?title=${encodeURIComponent(ev.title)}` +
      `&st=${fmtCompactUtc(ev.start)}` +
      `&et=${fmtCompactUtc(ev.end)}` +
      `&desc=${encodeURIComponent(ev.description)}` +
      `&in_loc=${encodeURIComponent(ev.location)}`;
    openHref(href);
  };

  const handleIcs = () => {
    const ev = buildEvent();
    if (!ev) return;
    downloadIcs({ ...ev, filename });
    setOpen(false);
  };

  const options = [
    {
      name: "Google Calendar", color: "#4285F4", action: handleGoogle, icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M19.5 3h-15A1.5 1.5 0 0 0 3 4.5v15A1.5 1.5 0 0 0 4.5 21h15a1.5 1.5 0 0 0 1.5-1.5v-15A1.5 1.5 0 0 0 19.5 3zM10.5 16.5h-1.5v-6L7.5 11v-1.5l3-1.5h.75v8.25zm5.625-1.219c-.219.703-.797 1.219-1.594 1.219-.844 0-1.547-.469-1.781-1.219l1.359-.469c.094.328.281.469.469.469.281 0 .469-.281.469-.656 0-.422-.187-.703-.516-.703h-.469v-1.219h.469c.281 0 .422-.234.422-.563 0-.328-.141-.563-.422-.563-.234 0-.375.141-.469.422l-1.312-.469c.234-.75.891-1.219 1.781-1.219.797 0 1.453.469 1.547 1.219.094.609-.234 1.078-.609 1.359.469.281.797.797.703 1.453z" /></svg>
      )
    },
    {
      name: "Outlook / Office 365", color: "#0078D4", action: handleOutlook, icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M21.17 5.17H10.83A1.83 1.83 0 0 0 9 7v10a1.83 1.83 0 0 0 1.83 1.83h10.34A1.83 1.83 0 0 0 23 17V7a1.83 1.83 0 0 0-1.83-1.83zM16 15.5A3.5 3.5 0 1 1 19.5 12 3.5 3.5 0 0 1 16 15.5zM7 4l-6 1.07v13.86L7 20zm-3.65 9.91c-.4 0-.72-.18-.96-.52a2.34 2.34 0 0 1-.36-1.39 2.5 2.5 0 0 1 .37-1.43c.25-.36.58-.54 1-.54s.71.17.94.5a2.4 2.4 0 0 1 .35 1.4 2.5 2.5 0 0 1-.36 1.43c-.24.37-.57.55-.98.55zM16 10.5A1.5 1.5 0 1 0 17.5 12 1.5 1.5 0 0 0 16 10.5z" /></svg>
      )
    },
    {
      name: "Yahoo Calendar", color: "#6001D2", action: handleYahoo, icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M13.13 2.5h4.94l-5.4 12.93h-1.34L13.13 2.5zm-2.27 5.06L7.4 14.86 4.05 7.56H.5l5.31 11.13L3.5 24h3.46L13.86 7.56h-3zm10.59 5.6a1.84 1.84 0 1 0 1.85 1.84 1.85 1.85 0 0 0-1.85-1.84z" /></svg>
      )
    },
    {
      name: "Apple Calendar", color: "#A2AAAD", action: handleIcs, icon: (
        <svg viewBox="0 0 24 24" className="w-4 h-4" fill="currentColor"><path d="M17.05 12.04c-.03-2.89 2.36-4.28 2.47-4.34-1.35-1.97-3.45-2.24-4.19-2.27-1.79-.18-3.49 1.05-4.4 1.05-.91 0-2.31-1.02-3.8-.99-1.96.03-3.76 1.14-4.77 2.89-2.03 3.52-.52 8.74 1.46 11.6.96 1.4 2.11 2.97 3.62 2.91 1.45-.06 2-.94 3.76-.94 1.75 0 2.25.94 3.79.91 1.56-.03 2.55-1.42 3.51-2.83 1.1-1.62 1.56-3.2 1.59-3.28-.03-.02-3.05-1.17-3.04-4.71zM14.21 3.65c.8-.97 1.34-2.32 1.19-3.65-1.15.05-2.55.77-3.38 1.74-.74.86-1.39 2.24-1.21 3.55 1.28.1 2.59-.65 3.4-1.64z" /></svg>
      )
    },
    {
      name: "Download .ics", color: "#ccd6f6", action: handleIcs, icon: <Download className="w-4 h-4" />
    },
  ];

  const triggerClass =
    buttonClassName ||
    "w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-[13px] font-[\"Open_Sans\",sans-serif] text-[#ccd6f6] transition-colors hover:bg-[rgba(106,31,191,0.18)]";
  const triggerStyle = buttonClassName
    ? undefined
    : { backgroundColor: "#0d1240", border: "1px solid rgba(106,31,191,0.35)" };

  return (
    <div ref={containerRef} className="relative">
      <button type="button" onClick={() => setOpen((o) => !o)} className={triggerClass} style={triggerStyle}>
        <CalendarPlus className="w-4 h-4" /> {label}
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          ref={menuRef}
          className="absolute left-0 right-0 top-full mt-2 z-30 rounded-xl overflow-hidden shadow-xl"
          style={{ backgroundColor: "#0d1240", border: "1px solid rgba(106,31,191,0.35)" }}
        >
          {options.map((opt) => (
            <button
              key={opt.name}
              type="button"
              onClick={opt.action}
              className='w-full flex items-center gap-3 px-4 py-2.5 text-left text-[13px] font-["Open_Sans",sans-serif] text-[#ccd6f6] transition-colors hover:bg-[rgba(106,31,191,0.18)]'
            >
              <span style={{ color: opt.color }} className="flex items-center">{opt.icon}</span>
              <span>{opt.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default CalendarShareDropdown;
